import { useInternalChat } from '@/hooks/useInternalChat';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMessageTranslation } from '@/hooks/useMessageTranslation';
import { useMessageNotifications, clearTabBadge } from '@/hooks/useMessageNotifications';
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Inbox, MessageSquare, Send, Loader2, Check, CheckCheck } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const NAVY = '#1D314D';
const GOLD = '#CEAB65';

function getInitials(name: string) {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

export default function AdminInbox() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const {
    conversations, selectedConversation, messages,
    loadingConversations, loadingMessages, sending,
    selectConversation, sendMessage,
  } = useInternalChat() as any;

  const [newMsg, setNewMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const { getDisplay } = useMessageTranslation(messages);
  useMessageNotifications();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (selectedConversation) clearTabBadge();
  }, [selectedConversation]);

  const filtered = (conversations || []).filter((c: any) => {
    if (!searchTerm) return true;
    return (c.title || '').toLowerCase().includes(searchTerm.toLowerCase())
      || (c.lastMessage || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getConvTitle = (conv: any) => {
    if (conv.title) return conv.title;
    const other = conv.participants?.find((p: any) => p.userId !== user?.id);
    return other?.userName || 'Conversation';
  };

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    try {
      await sendMessage({ content: newMsg.trim() });
      setNewMsg('');
    } catch {
      toast.error("Erreur d'envoi");
    }
  };

  const totalUnread = (conversations || []).reduce((sum: number, c: any) => {
    const p = c.participants?.find((p: any) => p.userId === user?.id);
    return sum + (p?.unreadCount || 0);
  }, 0);

  const stats = {
    total: conversations?.length || 0,
    unread: totalUnread,
    today: (conversations || []).filter((c: any) => {
      const d = c.lastMessageAt ? new Date(c.lastMessageAt) : null;
      return d && d.toDateString() === new Date().toDateString();
    }).length,
  };

  if (loadingConversations) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Conversations', value: stats.total, accent: NAVY },
          { label: 'Non lus', value: stats.unread, accent: GOLD },
          { label: "Aujourd'hui", value: stats.today, accent: '#639922' },
        ].map((s, i) => (
          <div key={i} className="rounded-lg border bg-card p-3" style={{ borderLeft: `3px solid ${s.accent}` }}>
            <div className="text-xs text-muted-foreground">{s.label}</div>
            <div className="text-2xl font-semibold" style={{ color: s.accent }}>{s.value}</div>
          </div>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Inbox className="w-4 h-4" style={{ color: NAVY }} />
            Inbox Plateforme
            {totalUnread > 0 && (
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full text-white" style={{ background: GOLD }}>
                {totalUnread} non lus
              </span>
            )}
          </CardTitle>
          <CardDescription className="text-xs">
            Conversations internes avec les utilisateurs inscrits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-[300px_1fr] gap-3 h-[calc(100vh-340px)] min-h-[500px]">
            {/* Sidebar */}
            <div className="flex flex-col border rounded-lg overflow-hidden bg-background">
              <div className="p-2 border-b">
                <Input
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Rechercher..."
                  className="h-7 text-xs"
                />
              </div>
              <ScrollArea className="flex-1">
                {filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                    <MessageSquare className="w-8 h-8 mb-2 opacity-40" />
                    <p className="text-xs">Aucune conversation</p>
                  </div>
                ) : filtered.map((conv: any) => {
                  const isActive = conv.id === selectedConversation?.id;
                  const title = getConvTitle(conv);
                  const unread = conv.participants?.find((p: any) => p.userId === user?.id)?.unreadCount || 0;
                  const lastTime = conv.lastMessageAt ? format(new Date(conv.lastMessageAt), 'HH:mm') : '';
                  return (
                    <button
                      key={conv.id}
                      onClick={() => selectConversation(conv)}
                      className="w-full text-left transition-colors hover:bg-muted/40"
                      style={{
                        padding: '10px 12px',
                        borderBottom: '0.5px solid hsl(var(--border))',
                        background: isActive ? 'rgba(29,49,77,0.06)' : 'transparent',
                        borderLeft: isActive ? `2px solid ${GOLD}` : '2px solid transparent',
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <div className="relative flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold text-white" style={{ background: NAVY }}>
                          {getInitials(title)}
                          {unread > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: GOLD }}>
                              {unread}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs truncate" style={{ fontWeight: unread > 0 ? 600 : 500 }}>{title}</span>
                            {lastTime && <span className="text-[10px] text-muted-foreground flex-shrink-0">{lastTime}</span>}
                          </div>
                          {conv.lastMessage && (
                            <p className="text-[11px] text-muted-foreground truncate mt-0.5">{conv.lastMessage}</p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </ScrollArea>
            </div>

            {/* Thread */}
            <div className="flex flex-col border rounded-lg overflow-hidden bg-background">
              {!selectedConversation ? (
                <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                  <MessageSquare className="w-10 h-10 mb-2 opacity-30" />
                  <p className="text-sm">Sélectionnez une conversation</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-3 border-b">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white" style={{ background: NAVY }}>
                      {getInitials(getConvTitle(selectedConversation))}
                    </div>
                    <span className="text-sm font-medium">{getConvTitle(selectedConversation)}</span>
                  </div>

                  <ScrollArea className="flex-1 p-3">
                    {loadingMessages ? (
                      <div className="flex justify-center py-10">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex justify-center py-10">
                        <p className="text-xs text-muted-foreground">Aucun message pour le moment</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map((msg: any) => {
                          const isOwn = msg.fromUserId === user?.id;
                          const isRead = Array.isArray(msg.readBy) && msg.readBy.length > 1;
                          return (
                            <div key={msg.id} className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`}>
                              {!isOwn && (
                                <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white" style={{ background: NAVY }}>
                                  {getInitials(msg.fromUserName || '?')}
                                </div>
                              )}
                              <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'} flex flex-col`}>
                                {!isOwn && <span className="text-[10px] text-muted-foreground mb-0.5">{msg.fromUserName}</span>}
                                <div
                                  className="rounded-lg px-3 py-2 text-sm"
                                  style={{
                                    background: isOwn ? NAVY : 'hsl(var(--muted))',
                                    color: isOwn ? '#fff' : 'inherit',
                                  }}
                                >
                                  {(() => {
                                    const d = getDisplay(msg);
                                    return (
                                      <>
                                        {d.text}
                                        {d.translated && (
                                          <div
                                            className="mt-1 pt-1 border-t border-white/10 text-[10px] italic opacity-70"
                                            title={d.original}
                                          >
                                            {language === 'fr' ? 'Traduit automatiquement' : 'Auto-translated'}
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                                <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
                                  <span>{format(new Date(msg.createdAt), 'HH:mm')}</span>
                                  {isOwn && (isRead
                                    ? <CheckCheck className="w-3 h-3" style={{ color: GOLD }} />
                                    : <Check className="w-3 h-3" />)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </ScrollArea>

                  <div className="p-3 border-t">
                    <div className="flex gap-2">
                      <Input
                        value={newMsg}
                        onChange={e => setNewMsg(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        placeholder="Tapez un message..."
                        className="flex-1 text-sm h-9"
                        disabled={sending}
                      />
                      <Button onClick={handleSend} disabled={sending || !newMsg.trim()} size="sm" style={{ background: NAVY }}>
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
